/**
 * Utility functions for managing report access
 */

import { getAllUrlParameters, getUserOSName } from '../../../utils/BrowserUtils';
import { getUserLocation } from '../../../utils/UserLocationUtils';

/**
 * Check if a user has access to a specific report
 * @param reportId Unique identifier for the report
 * @returns Boolean indicating if the user has access
 */
export const hasReportAccess = (reportId: string): boolean => {
  if (typeof window === 'undefined') return false;

  const storageKey = `report_access_${reportId}`;
  return localStorage.getItem(storageKey) === 'true';
};

/**
 * Grant access to a specific report
 * @param reportId Unique identifier for the report
 * @param email User's email (optional)
 */
export const grantReportAccess = (reportId: string, email?: string): void => {
  if (typeof window === 'undefined') return;

  const storageKey = `report_access_${reportId}`;
  localStorage.setItem(storageKey, 'true');

  if (email) {
    localStorage.setItem(`${storageKey}_email`, email);
  }
};

/**
 * Submit an email to get access to a report
 * @param reportId Unique identifier for the report
 * @param email User's email
 * @returns Promise that resolves when the API call is complete
 */
export const submitEmailForReportAccess = async (reportId: string, email: string): Promise<boolean> => {
  try {
    // Get URL parameters and user info
    const urlParams = getAllUrlParameters();
    const campaign = { ...urlParams };

    // Remove sensitive fields from campaign data
    delete campaign.email;
    delete campaign.phone;
    delete campaign.source;
    console.log('Debug - URL Parameters:', urlParams);
    console.log('Debug - Campaign after cleanup:', campaign);
    // Send email to backend API with URL parameters
    const response = await fetch('https://api.premagic.com/website/report/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        report_name: reportId,
        email_id: email,
        platform: getUserOSName(),
        source: urlParams["source"] || (typeof document !== 'undefined' ? document.referrer : ''),
        campaign: JSON.stringify(campaign),
        location: JSON.stringify(getUserLocation())
      })
    });

    const data = await response.json();

    // Check if API returned success
    if (response.status === 200 && data.status === 'ok') {
      // Store access for this specific report
      grantReportAccess(reportId, email);
      return true;
    } else {
      // Handle API error
      console.error('API error:', data);
      return false;
    }
  } catch (error) {
    // Handle network errors
    console.error('Network error:', error);
    return false;
  }
};
